import { GenericTable } from "@shared/components/GenericTable";
import { toast } from "@shared/hooks/use-toast";
import { TableColumn } from "@shared/types/table";
import { Button } from "@shared/ui/button";
import { PAGE_SIZE } from "@shared/utils/constants";
import { MapPin, X } from "lucide-react";
import { useMemo, useState } from "react";
import { useGetAssets } from "../../hooks";
import { AssetSearchFilters } from "./assetFilter";

interface AssetSearchFiltersType {
  type: string;
  facility: string;
  system: string;
  network: string;
  location: string;
  status: string;
  page: number;
  latitude: any;
  longitude: any;
}

const getAssetTypeBadgeConfig = (type: string) => {
  switch (type?.toLowerCase()) {
    case "pipe":
      return {
        variant: "default" as const,
        className: "bg-blue-100 text-blue-800",
      };
    case "hydrant":
      return {
        variant: "secondary" as const,
        className: "bg-red-100 text-red-800",
      };
    case "valve":
      return {
        variant: "outline" as const,
        className: "bg-green-100 text-green-800",
      };
    case "meter":
      return {
        variant: "default" as const,
        className: "bg-purple-100 text-purple-800",
      };
    default:
      return {
        variant: "outline" as const,
        className: "bg-gray-100 text-gray-800",
      };
  }
};

interface AssetSearchTableProps {
  onAssetSelect?: (asset: any) => void;
  searchLocation?: {
    latitude?: number;
    longitude?: number;
    address?: string;
  };
  linkedAsset: any;
  setLinkedAsset: any;
}

export const AssetSearchTable = ({
  onAssetSelect,
  searchLocation,
  linkedAsset,
  setLinkedAsset,
}: AssetSearchTableProps) => {
  const [currentPage, setCurrentPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState("");
  const [sortField, setSortField] = useState<string | null>(null);
  const [sortConfig, setSortConfig] = useState<{
    field: string;
    direction: "asc" | "desc";
  } | null>(null);
  const [filters, setFilters] = useState<AssetSearchFiltersType>({
    type: "all",
    facility: "all",
    system: "all",
    network: "all",
    location: "",
    status: "all",
    page: 1,
    latitude: 0,
    longitude: 0,
  });

  // Create API parameters with pagination and search
  const apiParams = useMemo(() => {
    return {
      ...filters,
      page: currentPage,
      limit: PAGE_SIZE,
      search: searchTerm,
      sort: sortField,
    };
  }, [filters, currentPage, searchTerm, sortField]);

  // Fetch data with current filters and pagination
  const { data: assetData } = useGetAssets(apiParams);

  const handleSearch = (searchValue: string) => {
    setSearchTerm(searchValue);
    setCurrentPage(1); // Reset to first page when search changes
  };

  const handleFiltersChange = (newFilters: AssetSearchFiltersType) => {
    setFilters(newFilters);
    setCurrentPage(1); // Reset to first page when filters change
  };

  const clearFilters = () => {
    setFilters({
      type: "all",
      facility: "all",
      system: "all",
      network: "all",
      location: "",
      status: "all",
      page: 1,
      latitude: 0,
      longitude: 0,
    });
    setSearchTerm("");
    setSortField(null);
    setSortConfig(null);
    setCurrentPage(1);
  };

  const handlePageChange = (newPage: number) => {
    setCurrentPage(newPage);
  };

  const handleSort = (field: string, direction: "asc" | "desc") => {
    setSortField(direction === "asc" ? `+${field}` : `-${field}`);
    setSortConfig({ field, direction });
    setCurrentPage(1); // Reset to first page when sort changes
  };

  const handleAssetSelectAction = (row: any) => {
    setLinkedAsset(row);
    if (onAssetSelect) onAssetSelect(row);
  };

  const handleClearSelection = () => {
    setLinkedAsset(null);
  };

  const columns: TableColumn<any>[] = [
    {
      key: "assetNo",
      header: "Asset ID",
      type: "text",
      render: (value: string) => <div className="font-medium">{value}</div>,
    },
    {
      key: "assetName",
      header: "Asset Name",
      type: "text",
    },
    {
      key: "assetTypeDisplay",
      header: "Type",
      type: "badge",
      badgeConfig: {
        getVariant: (value: string) => getAssetTypeBadgeConfig(value).variant,
        getClassName: (value: string) =>
          getAssetTypeBadgeConfig(value).className,
      },
    },
    {
      key: "location",
      header: "Location",
      type: "custom",
      render: (value: string) => (
        <span
          title={value}
          className="inline-block max-w-[200px] overflow-hidden text-ellipsis whitespace-nowrap cursor-pointer hover:bg-gray-100 hover:text-gray-900 transition-colors duration-200 px-1 py-1 rounded"
        >
          {value?.length > 15 ? `${value.substring(0, 15)}...` : value || "N/A"}
        </span>
      ),
    },
    {
      key: "distance",
      header: "Distance",
      type: "custom",
    },
    {
      key: "statusDisplay",
      header: "Status",
      type: "text",
      render: (value: string) => (
        <div
          className={`text-sm font-medium capitalize ${
            value === "active"
              ? "text-green-600"
              : value === "inactive"
              ? "text-red-600"
              : "text-gray-600"
          }`}
        >
          {value}
        </div>
      ),
    },
    {
      key: "actions",
      header: "Actions",
      type: "custom",
      render: (_, row) => (
        <button
          onClick={() => handleAssetSelectAction(row)}
          className="px-3 py-1 text-sm bg-blue-600 text-white rounded hover:bg-blue-700"
        >
          Select
        </button>
      ),
    },
  ];

  // Calculate pagination info using API data
  const totalItems = assetData?.count || 0;
  const totalPages = Math.ceil(totalItems / PAGE_SIZE);
  const startItem = totalItems > 0 ? (currentPage - 1) * PAGE_SIZE + 1 : 0;
  const endItem = Math.min(currentPage * PAGE_SIZE, totalItems);

  const getAssetsNearLocation = () => {
    if (!searchLocation?.latitude || !searchLocation?.longitude) {
      toast({
        title: "Set location before searching nearby assets...",
        variant: "default",
      });
      return;
    }
    setFilters({
      ...filters,
      latitude: searchLocation.latitude,
      longitude: searchLocation.longitude,
    });
    setCurrentPage(1); // Reset to first page when location search is triggered
  };

  return (
    <div className="space-y-4">
      <div className="flex gap-4">
        <Button
          type="button"
          variant="outline"
          className="flex items-center gap-2"
          onClick={getAssetsNearLocation}
        >
          <MapPin className="w-4 h-4" />
          Find Assets Near Location
        </Button>
      </div>

      {linkedAsset ? (
        <div className="bg-blue-50 border border-blue-200 p-3 rounded-md flex justify-between items-center">
          <div className="text-sm">
            <strong>Linked Asset:</strong> {linkedAsset.assetId} -{" "}
            {linkedAsset.assetName} - {linkedAsset.location}
          </div>
          <button
            onClick={handleClearSelection}
            className="text-gray-500 hover:text-red-600"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      ) : (
        <GenericTable
          data={assetData?.results || []}
          columns={columns}
          rowKey="id"
          searchPlaceholder="Search by Asset ID or Name..."
          searchParamName="query"
          searchDebounceMs={300}
          searchValue={searchTerm}
          onSearch={handleSearch}
          onSort={handleSort}
          sortConfig={sortConfig}
          actionsType="buttons"
          bulkActions={[]}
          emptyMessage="No assets found"
          filterPanel={
            <AssetSearchFilters
              filters={filters}
              onFiltersChange={handleFiltersChange}
              onApplyFilters={handleFiltersChange}
              onClearFilters={clearFilters}
            />
          }
          pagination={{
            currentPage,
            totalPages,
            totalItems,
            itemsPerPage: PAGE_SIZE,
            startItem,
            endItem,
          }}
          onPageChange={handlePageChange}
        />
      )}
    </div>
  );
};
